import React from 'react'
import { Col, Row, Button } from 'reactstrap'
import { withFirebase } from '../../components/Fb'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import ActivityCreator from '../../components/Details/ActivityCreator'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import { imageThumbnailUrl } from '../../components/utils/images'
import {
  activityFromTemplate,
  defaultTemplateStart,
  formatDuration,
  formatRelative,
  recurrentFromTemplate,
} from '../../components/utils/activityTemplates'
import { DEFAULT_SPORTS, DEFAULT_TYPES } from '../../sports'

// Aktiviteetit > Pohjat: activity templates (docs/features/activity-templates.md). A template
// is everything about an activity except its dates; it is dropped onto the activity calendar
// to create an activity at that time.
class ActivityTemplatesContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      templates: [],
      // null = the list; {} = a new template; a template = editing it
      editing: null,
      // { activity, weekday, template } = a recurring series being created from a template
      series: null,
      // { activity, template } = a one-off activity being created from a template
      single: null,
      deleteTarget: null,
      images: [],
      sports: DEFAULT_SPORTS,
      activitytypes: DEFAULT_TYPES,
    }
  }

  componentDidMount() {
    const { firebase } = this.props
    this.unsubscribers = [
      firebase.listenActivityTemplates().onSnapshot((snapshot) => {
        const templates = snapshot.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .filter((t) => t.state !== 'deleted')
          .sort((a, b) => (a.templatename || '').localeCompare(b.templatename || ''))
        this.setState({ templates, loading: false })
      }),
      firebase.listenMedia().onSnapshot((snapshot) => {
        const images = []
        snapshot.forEach((doc) => {
          if ((doc.data().mimetype || '').indexOf('image/') > -1) images.push({ id: doc.id, ...doc.data() })
        })
        this.setState({ images })
      }),
      firebase.getSports().onSnapshot((doc) => this.setState({ sports: mergeOptions(DEFAULT_SPORTS, doc) })),
      firebase.getActivitytypes().onSnapshot((doc) => this.setState({ activitytypes: mergeOptions(DEFAULT_TYPES, doc) })),
    ]
  }

  componentWillUnmount() {
    ;(this.unsubscribers || []).forEach((unsubscribe) => unsubscribe && unsubscribe())
  }

  saveTemplate = (template) => {
    const { firebase } = this.props
    return template.id ? firebase.updateActivityTemplate(template) : firebase.createActivityTemplate(template)
  }

  // A one-off activity from the template, starting tomorrow at 18:00 for the user to move;
  // enrolment and the cancellation deadline follow the start (followStart).
  startSingle = (template) => {
    this.setState({ single: { activity: activityFromTemplate(template, defaultTemplateStart()), template } })
  }

  renderSingle() {
    const { single } = this.state
    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button onClick={() => this.setState({ single: null })}>Palaa pohjiin</Button>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Aktiviteetti pohjasta {single.template.templatename}. Valitse päivä ja aika; ilmoittautumisen ja
              peruutuksen ajat siirtyvät alun mukana.
              {(single.template.resourceids || []).length > 0
                ? ' Kenttiä ei varata tällä polulla: pudota pohja aktiviteettikalenteriin tai varaa kentät aktiviteetin Varaukset-välilehdeltä.'
                : ''}
            </Typography>
          </Col>
        </Row>
        <ActivityCreator
          initialActivity={single.activity}
          followStart
          onCreate={this.props.firebase.createActivity}
          onSaved={() => this.setState({ single: null })}
          activitytypes={this.state.activitytypes}
          sports={this.state.sports}
          images={this.state.images}
          customerdata={this.props.customerdata}
        />
      </div>
    )
  }

  // A recurring series from the template: today, 18:00, weekly; the recurring creator lets
  // the user pick the weekday, times and dates.
  startSeries = (template) => {
    const { activity, weekday } = recurrentFromTemplate(template)
    this.setState({ series: { activity, weekday, template } })
  }

  renderSeries() {
    const { series } = this.state
    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button onClick={() => this.setState({ series: null })}>Palaa pohjiin</Button>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Toistuva aktiviteetti pohjasta {series.template.templatename}. Valitse viikonpäivä, kellonajat ja
              toistuvuuden päivät.
              {(series.template.resourceids || []).length > 0 ? ' Toistuvalle sarjalle kenttiä ei varata automaattisesti.' : ''}
            </Typography>
          </Col>
        </Row>
        <ActivityCreator
          recurrent
          initialActivity={series.activity}
          initialWeekday={series.weekday}
          onCreate={this.props.firebase.createRecurrentActivity}
          onSaved={() => this.setState({ series: null })}
          activitytypes={this.state.activitytypes}
          sports={this.state.sports}
          images={this.state.images}
          customerdata={this.props.customerdata}
        />
      </div>
    )
  }

  confirmDelete = async () => {
    const target = this.state.deleteTarget
    this.setState({ deleteTarget: null })
    if (target) await this.props.firebase.deleteActivityTemplate(target.id)
  }

  resourceNames = (ids) => {
    const resources = (this.props.customerdata && this.props.customerdata.resources) || []
    return (ids || []).map((id) => {
      const r = resources.find((x) => x.id === id)
      return r ? r.displayName || r.name : id
    })
  }

  columns = () => [
    {
      headerName: '',
      field: 'actions',
      // four buttons on one line; the cell would clip anything that wraps
      width: 470,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', height: '100%' }}>
          <EditButton disablediv onClick={() => this.setState({ editing: params.row })} />
          <Button size='sm' color='info' onClick={() => this.startSingle(params.row)}>
            Luo aktiviteetti
          </Button>
          <Button size='sm' color='info' onClick={() => this.startSeries(params.row)}>
            Luo toistuva
          </Button>
          <DeleteButton disablediv onClick={() => this.setState({ deleteTarget: params.row })} />
        </Box>
      ),
    },
    {
      headerName: 'Kuva',
      field: 'poster',
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const src = imageThumbnailUrl(params.row.media) || params.row.poster
        if (!src) return null
        return (
          <Box
            component='img'
            src={src}
            alt=''
            loading='lazy'
            sx={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 0.5, display: 'block', mt: '4px' }}
          />
        )
      },
    },
    { headerName: 'Pohja', field: 'templatename', width: 220 },
    { headerName: 'Laji', field: 'sport', minWidth: 110 },
    { headerName: 'Kesto', field: 'duration', minWidth: 90, valueFormatter: (value) => formatDuration(value) },
    { headerName: 'Hinta', field: 'price', minWidth: 80, valueFormatter: (value) => (value || value === 0 ? Number(value).toFixed(2) + ' €' : '') },
    {
      headerName: 'Ilmoittautuminen',
      field: 'entrystart',
      minWidth: 300,
      sortable: false,
      valueGetter: (value, row) => formatRelative(row.entrystart) + ' – ' + formatRelative(row.entryend),
    },
    {
      headerName: 'Peruutus päättyy',
      field: 'cancellationend',
      minWidth: 170,
      valueGetter: (value, row) =>
        value === null || value === undefined || value === '' ? 'Sama kuin ilmoittautuminen' : formatRelative(value),
    },
    { headerName: 'Julkaisu', field: 'publish', minWidth: 100, valueFormatter: (value) => (value ? 'Heti' : 'Luonnos') },
    {
      headerName: 'Kentät',
      field: 'resourceids',
      minWidth: 140,
      sortable: false,
      valueGetter: (value) => this.resourceNames(value).join(', '),
    },
  ]

  renderEditor() {
    const { editing } = this.state
    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button onClick={() => this.setState({ editing: null })}>Palaa pohjiin</Button>
          </Col>
        </Row>
        <ActivityCreator
          key={editing.id || 'new'}
          template
          initialActivity={editing.id ? editing : null}
          onCreate={this.saveTemplate}
          onSaved={() => this.setState({ editing: null })}
          activitytypes={this.state.activitytypes}
          sports={this.state.sports}
          images={this.state.images}
          customerdata={this.props.customerdata}
        />
      </div>
    )
  }

  render() {
    if (this.state.editing) return this.renderEditor()
    if (this.state.series) return this.renderSeries()
    if (this.state.single) return this.renderSingle()
    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button size='sm' onClick={() => this.setState({ editing: {} })}>
              Lisää pohja
            </Button>
          </Col>
        </Row>
        <Row className='mb-2'>
          <Col>
            <Typography variant='body2' color='text.secondary'>
              Pohja sisältää aktiviteetin tiedot ilman päivämääriä. Ilmoittautumisen ja peruutuksen ajat ovat
              suhteessa alkuun. Pudota pohja Aktiviteetit &gt; Kalenteri -näkymässä haluamaasi kohtaan, niin
              aktiviteetti luodaan siihen aikaan.
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col>
            <div style={{ backgroundColor: '#fff' }}>
              <DataGrid
                autoHeight
                density='compact'
                rows={this.state.templates}
                columns={this.columns()}
                loading={this.state.loading}
                disableRowSelectionOnClick
                localeText={{ noRowsLabel: 'Ei pohjia. Lisää ensimmäinen pohja tai tallenna aktiviteetti pohjaksi.' }}
              />
            </div>
          </Col>
        </Row>
        <Dialog open={!!this.state.deleteTarget} onClose={() => this.setState({ deleteTarget: null })}>
          <DialogTitle>Poistetaanko pohja?</DialogTitle>
          <DialogContent>
            <Typography>
              {this.state.deleteTarget ? this.state.deleteTarget.templatename : ''}. Pohjasta jo luodut aktiviteetit
              eivät muutu.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color='link' onClick={() => this.setState({ deleteTarget: null })}>
              Peruuta
            </Button>
            <Button color='danger' onClick={this.confirmDelete}>
              Poista
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

// config/sports and config/activitytypes extend the built-in lists
const mergeOptions = (defaults, doc) => {
  const options = [...defaults]
  if (doc.exists && Array.isArray(doc.data().list)) options.push(...doc.data().list)
  const unique = [...new Map(options.map((item) => [item.key, item])).values()]
  return unique.sort((a, b) => a.label.localeCompare(b.label))
}

export default withFirebase(ActivityTemplatesContainer)
